<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useSession } from '../auth';
import AppHeader from '@/components/layout/AppHeader.vue';
import { Button } from '@/components/ui/button';

type Tab = 'claude' | 'claude-code' | 'chatgpt';

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const activeTab = ref<Tab>('claude');
const error = ref('');
const copiedField = ref<string | null>(null);

async function copyToClipboard(text: string, fieldName: string) {
  await navigator.clipboard.writeText(text);
  copiedField.value = fieldName;
  setTimeout(() => {
    copiedField.value = null;
  }, 2000);
}

onMounted(() => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
  }
});
</script>

<template>
  <div class="min-h-screen bg-background">
    <AppHeader />

    <main class="max-w-4xl mx-auto px-4 py-8">
      <div
        v-if="isLoading"
        class="text-center text-muted-foreground"
      >
        Loading...
      </div>

      <div
        v-else-if="user"
        class="space-y-6"
      >
        <div>
          <h2 class="text-2xl font-semibold">
            Integrations
          </h2>
          <p class="text-muted-foreground mt-1">
            Connect LLM-SR to your favorite AI tools
          </p>
        </div>

        <!-- Tabs -->
        <div class="border-b border-border">
          <nav class="flex gap-4">
            <button
              :class="[
                'px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors cursor-pointer',
                activeTab === 'claude'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              ]"
              @click="activeTab = 'claude'"
            >
              Claude
            </button>
            <button
              :class="[
                'px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors cursor-pointer',
                activeTab === 'claude-code'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              ]"
              @click="activeTab = 'claude-code'"
            >
              Claude Code
            </button>
            <button
              :class="[
                'px-4 py-2 font-medium text-sm border-b-2 -mb-px transition-colors cursor-pointer',
                activeTab === 'chatgpt'
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              ]"
              @click="activeTab = 'chatgpt'"
            >
              ChatGPT
            </button>
          </nav>
        </div>

        <div
          v-if="error"
          class="bg-destructive/10 text-destructive p-4 rounded-md"
        >
          {{ error }}
        </div>

        <!-- Claude Tab -->
        <div
          v-if="activeTab === 'claude'"
          class="space-y-6"
        >
          <div class="bg-card rounded-lg p-6 border border-border">
            <h3 class="font-semibold text-lg mb-2">
              Connect Claude to LLM-SR
            </h3>
            <p class="text-muted-foreground mb-4">
              Add LLM-SR as a custom connector in Claude to manage your flashcards directly in conversations.
            </p>
          </div>

          <div class="bg-muted/50 rounded-lg p-4">
            <h4 class="font-semibold mb-3">
              Setup Instructions
            </h4>
            <ol class="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
              <li>Go to <strong class="text-foreground">Settings</strong> in Claude</li>
              <li>Click on <strong class="text-foreground">Connectors</strong></li>
              <li>Click <strong class="text-foreground">Add custom connector</strong></li>
              <li>Enter the following details:</li>
            </ol>

            <div class="mt-4 space-y-3">
              <div class="bg-background rounded border border-border p-3">
                <div class="flex justify-between items-center">
                  <div>
                    <span class="text-xs text-muted-foreground uppercase tracking-wide">Name</span>
                    <p class="font-mono text-sm mt-0.5">llm-sr</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    @click="copyToClipboard('llm-sr', 'name')"
                  >
                    {{ copiedField === 'name' ? 'Copied!' : 'Copy' }}
                  </Button>
                </div>
              </div>

              <div class="bg-background rounded border border-border p-3">
                <div class="flex justify-between items-center">
                  <div>
                    <span class="text-xs text-muted-foreground uppercase tracking-wide">URL</span>
                    <p class="font-mono text-sm mt-0.5">https://mcp.llm-sr.com/mcp</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    @click="copyToClipboard('https://mcp.llm-sr.com/mcp', 'url')"
                  >
                    {{ copiedField === 'url' ? 'Copied!' : 'Copy' }}
                  </Button>
                </div>
              </div>
            </div>

            <ol
              start="5"
              class="list-decimal list-inside space-y-2 text-sm text-muted-foreground mt-4"
            >
              <li>Click <strong class="text-foreground">Add</strong></li>
              <li>Click <strong class="text-foreground">Connect</strong> to start the sign-in process</li>
              <li>Sign in to LLM-SR to authorize Claude</li>
              <li>Start asking Claude to help you manage your flashcards!</li>
            </ol>
          </div>
        </div>

        <!-- Claude Code Tab -->
        <div
          v-if="activeTab === 'claude-code'"
          class="space-y-6"
        >
          <div class="bg-card rounded-lg p-6 border border-border">
            <h3 class="font-semibold text-lg mb-2">
              Connect Claude Code to LLM-SR
            </h3>
            <p class="text-muted-foreground mb-4">
              Add LLM-SR as an MCP server in Claude Code to manage flashcards from the command line.
            </p>
          </div>

          <div class="bg-muted/50 rounded-lg p-4">
            <h4 class="font-semibold mb-3">
              Setup Instructions
            </h4>
            <ol class="list-decimal list-inside space-y-3 text-sm text-muted-foreground">
              <li>Run <code class="bg-background px-1.5 py-0.5 rounded text-xs font-mono">/mcp</code> in Claude Code</li>
              <li>Select <strong class="text-foreground">Add new MCP server</strong></li>
              <li>Choose <strong class="text-foreground">Remote (HTTP)</strong></li>
              <li>Enter the following URL:</li>
            </ol>

            <div class="mt-3 bg-background rounded border border-border p-3">
              <div class="flex justify-between items-center">
                <p class="font-mono text-sm">https://mcp.llm-sr.com/mcp</p>
                <Button
                  variant="outline"
                  size="sm"
                  @click="copyToClipboard('https://mcp.llm-sr.com/mcp', 'mcpUrl')"
                >
                  {{ copiedField === 'mcpUrl' ? 'Copied!' : 'Copy' }}
                </Button>
              </div>
            </div>

            <ol
              start="5"
              class="list-decimal list-inside space-y-2 text-sm text-muted-foreground mt-4"
            >
              <li>Select <strong class="text-foreground">Authenticate</strong></li>
              <li>Sign in to LLM-SR in your browser to authorize access</li>
              <li>Return to Claude Code - you should see "Connected to llm-sr"</li>
            </ol>
          </div>

          <div class="bg-muted/50 rounded-lg p-4">
            <h4 class="font-semibold mb-2">
              Re-authenticating
            </h4>
            <p class="text-sm text-muted-foreground">
              If you need to re-authenticate, run <code class="bg-background px-1.5 py-0.5 rounded text-xs font-mono">/mcp</code>,
              select the llm-sr server, choose <strong class="text-foreground">Clear authentication</strong>,
              then <strong class="text-foreground">Authenticate</strong> again.
            </p>
          </div>
        </div>

        <!-- ChatGPT Tab -->
        <div
          v-if="activeTab === 'chatgpt'"
          class="space-y-6"
        >
          <div class="bg-card rounded-lg p-6 border border-border">
            <h3 class="font-semibold text-lg mb-2">
              Use the LLM-SR GPT
            </h3>
            <p class="text-muted-foreground mb-4">
              We've created a custom GPT that can manage your flashcards directly in ChatGPT.
            </p>
            <a
              href="https://chatgpt.com/g/g-699f7c72504081919362c9db5218d8c0-llm-sr"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 text-primary hover:underline"
            >
              Open LLM-SR GPT
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>

          <div class="bg-muted/50 rounded-lg p-4">
            <h4 class="font-semibold mb-2">
              How It Works
            </h4>
            <ol class="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Open the LLM-SR GPT using the link above</li>
              <li>When the GPT tries to access your flashcards, you'll be prompted to sign in to LLM-SR and authorize access</li>
              <li>Once authorized, start managing your flashcards with natural language</li>
            </ol>
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-center"
      >
        <p class="text-muted-foreground mb-4">
          You need to sign in to access this page.
        </p>
        <Button @click="router.push('/sign-in')">
          Sign In
        </Button>
      </div>
    </main>
  </div>
</template>
